import { useState, useCallback } from "react";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";
import type { TranscriptItem } from "../../translator/hooks/useTranslator";

export interface Meeting {
  _id: string | number;
  title: string;
  source_lang: string;
  target_lang: string;
  user_id: string;
  created_at: string;
}

export function useMeetingHistory(translatorServiceUrl: string) {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [loadingMeetingId, setLoadingMeetingId] = useState<string | number | null>(null);

  const clearMeetings = useCallback(() => setMeetings([]), []);

  const fetchMeetings = useCallback(async (query: string = "", isEn: boolean = false) => {
    setLoading(true);
    setError("");
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const searchParam = query ? `?search=${encodeURIComponent(query)}` : "";
      
      const res = await fetch(`${cleanedUrl}/meetings${searchParam}`, {
        method: "GET",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Server returned ${res.status} ${res.statusText}`
            : `El servidor devolvió ${res.status} ${res.statusText}`
        );
      }

      const data = await res.json();
      setMeetings(data.meetings || []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Failed to retrieve meeting list." : "Error al obtener la lista de reuniones."));
    } finally {
      setLoading(false);
    }
  }, [translatorServiceUrl]);

  const renameMeeting = useCallback(async (meetingId: string | number, newTitle: string, isEn: boolean = false) => {
    if (!newTitle.trim()) return false;
    
    setRenaming(true);
    setError("");

    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meetingId}`, {
        method: "PATCH",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Rename failed: ${res.status} ${res.statusText}`
            : `Error al renombrar: ${res.status} ${res.statusText}`
        );
      }

      setMeetings((prev) =>
        prev.map((m) => (m._id === meetingId ? { ...m, title: newTitle.trim() } : m))
      );
      return true;
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error renaming meeting." : "Error al renombrar la reunión."));
      return false;
    } finally {
      setRenaming(false);
    }
  }, [translatorServiceUrl]);

  const loadMeetingTranscripts = useCallback(async (
    meeting: Meeting,
    recording: boolean,
    isEn: boolean,
    onLoad: (meetingId: string, transcript: TranscriptItem[], srcLang: string, tgtLang: string) => void
  ) => {
    if (recording) {
      alert(
        isEn
          ? "Please stop the active recording before loading a past meeting."
          : "Por favor, detén la grabación activa antes de cargar una reunión anterior."
      );
      return false;
    }

    setLoadingMeetingId(meeting._id);
    setError("");

    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meeting._id}/transcripts`, {
        method: "GET",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Failed to load transcripts: ${res.status} ${res.statusText}`
            : `Error al cargar transcripciones: ${res.status} ${res.statusText}`
        );
      }

      const data = await res.json();
      const rawTranscripts = data.transcripts || [];

      // Map backend fields (original_text, translated_text) to local fields (orig, tran)
      const mappedTranscripts: TranscriptItem[] = rawTranscripts.map((t: any) => {
        const textOrig = t.original_text || "";
        const isMic = textOrig.startsWith("🎤 Yo:");
        return {
          timestamp: t.timestamp || "",
          orig: textOrig,
          tran: t.translated_text || "",
          isMic,
        };
      });

      onLoad(
        String(meeting._id),
        mappedTranscripts,
        meeting.source_lang,
        meeting.target_lang
      );

      return true;
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error loading transcripts." : "Error al cargar las transcripciones de la reunión."));
      return false;
    } finally {
      setLoadingMeetingId(null);
    }
  }, [translatorServiceUrl]);

  const loadMeetingSummary = useCallback(async (
    meeting: Meeting,
    recording: boolean,
    isEn: boolean,
    onOpenSummary: (meeting: Meeting, summaryText: string, htmlFilename?: string) => void
  ) => {
    if (recording) {
      alert(isEn ? "Stop current recording before loading a meeting." : "Debes detener la grabación actual antes de cargar otra reunión.");
      return false;
    }

    try {
      setLoadingMeetingId(meeting._id);
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = translatorServiceUrl.replace(/\/$/, "");
      const res = await fetch(`${cleanedUrl}/meetings/${meeting._id}`, {
        method: "GET",
        headers: authHeaders,
      });
      if (res.ok) {
        const data = await res.json();
        const summaryText = data.summary || data.meeting?.summary || "";
        const htmlFilename = data.html_filename || data.meeting?.html_filename || "";
        onOpenSummary(meeting, summaryText, htmlFilename);
        return true;
      } else {
        alert(isEn ? "Error loading meeting details." : "Error al cargar los detalles de la reunión.");
        return false;
      }
    } catch (err) {
      console.error(err);
      alert(isEn ? "Network error loading meeting details." : "Error de red al cargar los detalles de la reunión.");
      return false;
    } finally {
      setLoadingMeetingId(null);
    }
  }, [translatorServiceUrl]);

  return {
    meetings,
    loading,
    error,
    renaming,
    loadingMeetingId,
    setError,
    clearMeetings,
    fetchMeetings,
    renameMeeting,
    loadMeetingTranscripts,
    loadMeetingSummary,
  };
}
